import {
  canEquipInSlot,
  EQUIP_SLOTS,
  findFreePlacement,
  getItemDef,
  GRID_AREAS,
  type AreaId,
  type Inventory,
  type InventoryOp,
  type ItemDef,
  type PlacedItem,
  type TargetRef,
} from "@rietz/shared";
import { uiActions } from "../actions.js";
import { useHud, type OpenContainer } from "../store.js";
import { gridOfArea, instanceAt, unequipSpot, type ItemSource } from "./drag.js";
import { searchTargetDone, searchTargetId } from "./search.js";

/**
 * Quick transfer: "put that over there" as one op at a time. Ctrl+click and
 * "Alles nehmen" are the same machine: `nextQuickMoveOp` decides ONE op from
 * the state as it is now, and `quickMove` runs it, re-reads the store and asks
 * again — `tryOp` is synchronous and replaces the believed state in place.
 *
 * Where things go (not symmetric):
 *   crate  → the player's grids in canonical scan order (pockets, rig, pack);
 *   grids  → the open container, ONLY when one is open AND searched;
 *   box    → OUT, into the player's grids; the dead man's rig and pack too;
 *   slot   → what the menu's "Ablegen" does;
 *   body   → the canonical grids, else the first equipment slot that takes it
 *            — mirroring the server's pickup fallback.
 * NOTHING quick-moves INTO a box; putting things away is a drag because it is
 * a decision. A container takes its contents with it (`contents` rides on the
 * instance). MERGE BEATS MOVE, and `merge` is partial-friendly, so one click
 * can be several merges then a move. Termination: every accepted merge shrinks
 * or empties the source, a move or unequip ends the loop, a refusal is "no
 * progress"; the iteration cap is a backstop. No DOM — `zoneAt` is never called.
 */

/** Hard stop for the driver. A pocket grid cannot hold this many stacks. */
const MAX_QUICK_MOVE_OPS = 16;

/**
 * Where a quick transfer from `from` may put things, in the order tried. Empty
 * means nowhere, reported by doing nothing.
 */
export function quickMoveTargets(
  from: ItemSource,
  container: OpenContainer | null,
  /**
   * False while the open container's destination grid is still being searched.
   * Only the INTO direction is affected.
   */
  containerReady = true,
): AreaId[] {
  if (from.t === "box" || from.t === "bodySlot") return [...GRID_AREAS];
  if (from.t !== "grid") return [];
  if (from.area === "container") return [...GRID_AREAS];
  if (!containerReady) return [];
  return container === null ? [] : ["container"];
}

/**
 * The single next op of a quick transfer, or `null` when nothing is left (or
 * possible). Pure — the caller decides whether to spend it — so it is testable
 * without a DOM, store or socket.
 */
export function nextQuickMoveOp(
  inv: Inventory,
  container: OpenContainer | null,
  from: ItemSource,
  iid: string,
  containerReady = true,
): InventoryOp | null {
  const item = instanceAt(inv, container, from, iid);
  if (item === null) return null;
  const def = getItemDef(item.defId);
  if (def === null) return null;

  // Taking something off is the menu's "Ablegen", destination and all —
  // `unequipSpot` never considers the open container. A full bag comes off
  // wrapped around its contents.
  if (from.t === "slot") {
    const to = unequipSpot(inv, container, from.slot, def);
    return to === null ? null : { t: "unequip", slot: from.slot, to };
  }
  if (from.t === "mod") return null;

  const areas = quickMoveTargets(from, container, containerReady);
  if (areas.length === 0) return null;

  const ontoIid = mergeTarget(inv, container, areas, def, iid);
  if (ontoIid !== null) return { t: "merge", iid, ontoIid };

  const to = freeSpotIn(inv, container, areas, def);
  if (to !== null) return { t: "move", iid, to };

  // ONTO YOURSELF, as a last resort, only off a body: the pack of somebody who
  // has been looting is usually full. The first slot the def fits and finds
  // empty, in `EQUIP_SLOTS` order — the question `equip` asks.
  if (from.t === "bodySlot") {
    const slot =
      EQUIP_SLOTS.find((s) => canEquipInSlot(def, s) && inv.equipment[s] === null) ?? null;
    if (slot !== null) return { t: "equip", iid, slot };
  }
  return null;
}

/**
 * The stack this one should be poured into: the first destination area that has
 * one, top-left-most within a grid, so two runs pick the same target.
 */
function mergeTarget(
  inv: Inventory,
  container: OpenContainer | null,
  areas: readonly AreaId[],
  def: ItemDef,
  iid: string,
): string | null {
  if (def.stackMax <= 1) return null;

  for (const area of areas) {
    const grid = gridOfArea(inv, container, area);
    if (grid === null) continue;
    let best: PlacedItem | null = null;
    for (const p of grid.items) {
      // A stack at the cap has no room and an item cannot be poured into itself
      // —
      // `applyInventoryOp` refuses both.
      if (p.item.iid === iid || p.item.defId !== def.id || p.item.stack >= def.stackMax) continue;
      if (best === null || p.y < best.y || (p.y === best.y && p.x < best.x)) best = p;
    }
    if (best !== null) return best.item.iid;
  }
  return null;
}

/** First legal cell across `areas`, in the order given. */
function freeSpotIn(
  inv: Inventory,
  container: OpenContainer | null,
  areas: readonly AreaId[],
  def: ItemDef,
): TargetRef | null {
  for (const area of areas) {
    const grid = gridOfArea(inv, container, area);
    if (grid === null) continue;
    const spot = findFreePlacement(grid, def);
    if (spot !== null) return { area, x: spot.x, y: spot.y, rot: spot.rot };
  }
  return null;
}

/**
 * Runs a quick transfer to completion against the live store. Progress is
 * detected by REFERENCE: `tryOp` publishes fresh `inv`/`container` on success
 * and writes nothing on refusal.
 */
export function quickMove(from: ItemSource, iid: string): void {
  for (let i = 0; i < MAX_QUICK_MOVE_OPS; i++) {
    const before = useHud.getState();
    if (before.inv === null) return;

    // Re-asked every iteration: the search sweep can finish between two ops.
    // The target is the one thing anything is ever quick-moved INTO.
    const containerReady = searchTargetDone(
      before.containerSearch,
      searchTargetId(before.container, { area: "container" }),
    );

    const op = nextQuickMoveOp(before.inv, before.container, from, iid, containerReady);
    if (op === null) return;

    uiActions().tryOp(op);

    const after = useHud.getState();
    if (after.inv === before.inv && after.container === before.container) return;
    // Only a merge can leave something behind to keep going with; a move or an
    // unequip has put the item where it was going.
    if (op.t !== "merge") return;
  }
}

// ---------------------------------------------------------------------------
// Readouts
// ---------------------------------------------------------------------------

/**
 * Kilograms for a header or tooltip row. One decimal, except anything under 100
 * g keeps a second rather than reading "0,0 kg".
 */
export function formatKg(kg: number): string {
  const digits = kg > 0 && kg < 0.1 ? 2 : 1;
  return `${kg.toFixed(digits)} kg`;
}
